import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import useModuleAccess from '@/hooks/useModuleAccess';
import { useAuth } from '@/context/AuthContext';
import { Button } from './ui/button';

// URL first-segment (after tenant code) -> module code
const PATH_MODULE_MAP = {
  'attendance': 'attendance',
  'routine': 'routine',
  'examination': 'examination',
  'lms': 'lms',
  'hr': 'hr',
  'leave': 'leave',
  'library': 'library',
  'inventory': 'inventory',
  'hostel': 'hostel',
  'transport': 'transport',
  'communication': 'communication',
  'transcripts': 'transcripts',
  'placement': 'placement',
  'frontdesk': 'frontdesk',
  'user-management': 'user-management',
  'roles': 'roles',
  'consultants': 'consultants',
  'seat-booking': 'seat-booking',
  'admission': 'admission',
  'fees': 'fees',
  'accounts': 'accounts',
  'reports': 'reports'
};

// Two-segment sub-paths that belong to a module different from their parent prefix
const SUBPATH_MODULE_MAP = {
  'reports/inventory': 'inventory',
  'reports/library': 'library',
  'reports/hostel': 'hostel',
  'reports/transport': 'transport',
  'reports/staff-leave': 'leave',
  'reports/staff-attendance': 'hr',
  'reports/student-attendance': 'attendance',
  'reports/salary': 'hr',
  'reports/library-fee': 'library',
  'reports/library-fine': 'library',
  'reports/hostel-damage-fine': 'hostel',
  'reports/library-damage': 'library',
  'reports/inventory-damage': 'inventory',
  'master-settings/attendance-fine': 'attendance',
  'master-settings/salary-components': 'hr',
  'settings/attendance': 'hr'
};

export const ModuleRouteGuard = ({ children, tenantCode }) => {
  const location = useLocation();
  const { isModuleEnabled, loading, isSuperAdmin, isAdmin } = useModuleAccess();
  const { user } = useAuth();

  let path = location.pathname;
  if (tenantCode && path.startsWith(`/${tenantCode}/`)) {
    path = path.slice(tenantCode.length + 1);
  }
  const segs = path.split('/').filter(Boolean);
  const moduleCode = SUBPATH_MODULE_MAP[segs.slice(0, 2).join('/')] || PATH_MODULE_MAP[segs[0]];

  if (loading || !moduleCode || isSuperAdmin || isModuleEnabled(moduleCode)) {
    return children;
  }

  const canUpgrade = isAdmin || user?.role === 'Admin';

  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="module-disabled-screen">
      <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
        <Lock className="w-8 h-8 text-red-500" />
      </div>
      <h2 className="text-lg font-semibold text-gray-800 mb-2">Module Not Enabled</h2>
      <p className="text-sm text-gray-500 max-w-md mb-6">
        This module is not included in your current subscription plan.
        {canUpgrade ? ' Upgrade your plan to unlock it instantly.' : ' Please contact your administrator to get access.'}
      </p>

      {canUpgrade && (
        <div className="w-full max-w-md rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-5 mb-6 text-left" data-testid="module-upgrade-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-800 text-sm">Unlock more with an upgraded plan</p>
              <p className="text-xs text-gray-600 mt-1 mb-3">
                Get access to this module and more — Library, HR, Examination, Hostel, Transport and beyond.
              </p>
              <Link to="/purchase">
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="module-upgrade-btn">
                  Upgrade Plan <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      <Link to={tenantCode ? `/${tenantCode}/dashboard` : '/dashboard'}>
        <Button variant={canUpgrade ? 'outline' : 'default'} data-testid="module-disabled-back-btn">Go to Dashboard</Button>
      </Link>
    </div>
  );
};

export default ModuleRouteGuard;
